import type { InboxItem } from '../api/types.js';

// What waits on the operator, by kind (operator, 30/10/2026): the menu's shortcuts, each counting the To do decisions
// of its kind on the server shown, so there is one number (ADR-039 section 6), and opening To do on them.

export type WaitingKind = 'slack' | 'reviews' | 'deploys' | 'mail' | 'tickets' | 'sessions' | 'other';

export const WAITING: Array<{ key: WaitingKind; label: string; hint: string }> = [
  { key: 'slack', label: 'slack', hint: 'Slack conversations waiting on your reply' },
  { key: 'reviews', label: 'reviews', hint: 'Pull and merge requests waiting on your review' },
  { key: 'deploys', label: 'deploys', hint: 'Deployments waiting on your approval' },
  { key: 'mail', label: 'mail', hint: 'Emails waiting on your reply' },
  { key: 'tickets', label: 'tickets', hint: 'Tickets waiting on you' },
  { key: 'sessions', label: 'sessions', hint: 'Sessions waiting on you: a question, a held or blocked run, a failed CI' },
  { key: 'other', label: 'other', hint: 'Everything else that waits on you: proposals, calendar, credentials' },
];

const BY_KIND: Partial<Record<InboxItem['kind'], WaitingKind>> = {
  slack: 'slack',
  review: 'reviews', replies: 'reviews',
  deployment: 'deploys', analysis: 'deploys',
  mail: 'mail',
  ticket: 'tickets', epic: 'tickets', jira: 'tickets', request: 'tickets',
  waiting_for_input: 'sessions', question: 'sessions', session: 'sessions', held: 'sessions', blocked: 'sessions', ci: 'sessions',
};

/** The shortcut a decision counts in; every decision counts in one */
export function waitingOf(item: Pick<InboxItem, 'kind' | 'url'>): WaitingKind {
  // A GitLab line is a merge request to review or an issue (ADR-041)
  if (item.kind === 'gitlab') return /\/-\/merge_requests\//.test(item.url) ? 'reviews' : 'tickets';
  return BY_KIND[item.kind] ?? 'other';
}

export function isWaitingKind(s: string | null): s is WaitingKind {
  return WAITING.some((w) => w.key === s);
}

/** Per shortcut: how many, and whether one of them is urgent */
export function waitingCounts(items: ReadonlyArray<Pick<InboxItem, 'kind' | 'url' | 'urgent'>>): Record<WaitingKind, { count: number; urgent: boolean }> {
  const out = Object.fromEntries(WAITING.map((w) => [w.key, { count: 0, urgent: false }])) as Record<WaitingKind, { count: number; urgent: boolean }>;
  for (const i of items) {
    const c = out[waitingOf(i)];
    c.count += 1;
    if (i.urgent) c.urgent = true;
  }
  return out;
}

/** To do on one server's decisions of a kind */
export const todoOf = (serverId: string, kind?: WaitingKind) => `/?server=${encodeURIComponent(serverId)}${kind ? `&waiting=${kind}` : ''}`;
